import React from "react"
import dynamic from "next/dynamic"

import { useDispatch, useSelector } from "react-redux"

import { Formik } from "formik"
import * as Yup from "yup"
import { Form as FormikForm } from "formik"
import { Row, Card } from "react-bootstrap"

import * as ga from "@/ga"

import {
    resetMakeEnquiryForm,
    sendMakeEnquiryFormData,
} from "@/slices/makeEnquiryFormSlice"

import NameField from "@/fields/nameField"
import EmailField from "@/fields/emailField"
import PhoneField from "@/fields/phoneField"
import MessageField from "@/fields/messageField"
import Recaptcha from "@/fields/recaptcha"
import SubmitButton from "@/fields/submitButton"

const SuccessToast = dynamic(() => import("@/toasts/successToast"))
const ErrorToast = dynamic(() => import("@/toasts/errorToast"))

function MakeEnquiryForm() {
    const dispatch = useDispatch()
    const { cookieConsent } = useSelector((state) => state.cookie)

    const generateLead = () => {
        if (cookieConsent === "true") {
            ga.event({
                action: "generate_lead",
            })
        }
    }

    const initialValues = {
        fullName: "",
        email: "",
        phone: "",
        captcha: false,
        message: "",
    }

    // The selector to retrieve the make enquiry form from state.

    const { status } = useSelector((state) => state.makeEnquiryForm)

    // A regular expression that can detect a small number of fake mobile
    // numbers that will be used for validation (doesn't allow chars).
    const phoneRegExp =
        /^((\\+[1-9]{1,4}[ \\-]*)|(\\([0-9]{2,3}\\)[ \\-]*)|([0-9]{2,4})[ \\-]*)*?[0-9]{3,4}?[ \\-]*[0-9]{3,4}?$/

    // The schema used for validation of the request viewing form input.
    const MakeEnquirySchema = Yup.object().shape({
        fullName: Yup.string().required("This field is required."),
        email: Yup.string()
            .email("Must be a valid email.")
            .required("This field is required."),
        phone: Yup.string().matches(
            phoneRegExp,
            "Must be a valid phone number (digits only).",
        ),
        captcha: Yup.boolean().oneOf(
            [true],
            "Please confirm you are not a robot.",
        ),
        message: Yup.string().required("This field is required."),
    })

    let captcha
    let captchaMobile

    const setCaptchaRef = (ref) => {
        if (ref) {
            return (captcha = ref)
        }
    }
    const setCaptchaMobileRef = (ref) => {
        if (ref) {
            return (captchaMobile = ref)
        }
    }

    const resetCaptcha = () => {
        // maybe set it till after is submitted
        captcha.reset()
    }
    const resetCaptchaMobile = () => {
        // maybe set it till after is submitted
        captchaMobile.reset()
    }

    return (
        <>
            <Formik
                enableReinitialize={true}
                initialValues={initialValues}
                validationSchema={MakeEnquirySchema}
                // Send the form data to the backend
                onSubmit={async (values) => {
                    dispatch(sendMakeEnquiryFormData(values))
                }}
            >
                {(formik) => (
                    <FormikForm>
                        <SuccessToast
                            status={status}
                            resetModal={() => {
                                dispatch(resetMakeEnquiryForm())
                                formik.resetForm()
                                resetCaptcha()
                                resetCaptchaMobile()
                                generateLead()
                            }}
                        />
                        <ErrorToast
                            status={status}
                            resetModal={() => dispatch(resetMakeEnquiryForm())}
                        />
                        <Card className="card-col-lg mt-4">
                            <Card.Body>
                                <Card.Title>
                                    <p className="text-center">
                                        Simply enter your details and a member
                                        of our team will be in touch shortly.
                                    </p>
                                </Card.Title>

                                <Row>
                                    <NameField formik={formik} />

                                    <EmailField formik={formik} />
                                    <PhoneField formik={formik} />

                                    <MessageField formik={formik} />
                                </Row>

                                <div className="d-none d-sm-inline">
                                    <Recaptcha
                                        captchaRef={(r) => setCaptchaRef(r)}
                                        formik={formik}
                                        size="normal"
                                    />
                                </div>
                                <div className="d-sm-none">
                                    <Recaptcha
                                        captchaRef={(r) =>
                                            setCaptchaMobileRef(r)
                                        }
                                        formik={formik}
                                        size="compact"
                                    />
                                </div>

                                <SubmitButton
                                    submit_message="Send message"
                                    status={status}
                                    formik={formik}
                                />
                            </Card.Body>
                        </Card>
                    </FormikForm>
                    // Make Enquiry Form End
                )}
            </Formik>
        </>
    )
}

export default MakeEnquiryForm
